import { useRef } from 'react';
import { parseAdkEvent, AdkEventType } from '../adkEventParser';

/**
 * Format error message to be user-friendly
 * @param {string} error - Raw error message
 * @param {string} errorCode - Error code (e.g., "429", "RESOURCE_EXHAUSTED")
 * @param {string} status - Status code
 * @returns {string} Formatted error message
 */
// TODO: This is a temporary function to format error messages and display them in the chat for testing purposes.
// We should get consensus if we want to keep this function or not.
function formatErrorMessage(error, errorCode, status) {
  // Guard against undefined/null/empty error - required because we call error.includes() later
  if (!error || typeof error !== 'string') {
    return 'Sorry, I encountered an error. Please try again.';
  }

  // Handle 429 / Resource Exhausted errors
  if (
    errorCode === '429' ||
    errorCode === 'RESOURCE_EXHAUSTED' ||
    status === 'RESOURCE_EXHAUSTED' ||
    error.includes('429') ||
    error.includes('Resource exhausted') ||
    error.includes('RESOURCE_EXHAUSTED')
  ) {
    return 'Rate limit exceeded. The service is currently experiencing high demand. Please wait a moment and try again.';
  }

  // Handle other common errors
  if (errorCode === '500' || error.includes('500')) {
    return 'Server error. Please try again in a moment.';
  }

  if (errorCode === '503' || error.includes('503')) {
    return 'Service temporarily unavailable. Please try again later.';
  }

  // Extract user-friendly message from error text
  // Remove technical details like "Error while running agent: failed to call model:"
  let message = error;
  if (message.includes('Error while running agent:')) {
    message = message.replace(/Error while running agent:\s*/, '');
  }
  if (message.includes('failed to call model:')) {
    message = message.replace(/failed to call model:\s*/, '');
  }

  // If message is still too technical, provide a generic one
  if (message.length > 200 || message.includes('Status:') || message.includes('Details:')) {
    return 'An error occurred while processing your request. Please try again.';
  }

  return `An error occurred while processing your request: ${message}`;
}

/**
 * Custom hook to handle SSE streaming and event processing
 * Returns event handlers for ADK events
 */
export function useChatStream({
  setMessages,
  setToolCalls,
  allEventsRef,
  processedChunkIds,
  lastProcessedInvocationId
}) {
  const handleAdkEvent = event => {
    switch (event.type) {
      case AdkEventType.TEXT_MESSAGE_START:
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: '',
            timestamp: new Date()
          }
        ]);
        if (event.rawEvent?.invocationId) {
          lastProcessedInvocationId.current = event.rawEvent.invocationId;
        }
        break;

      case AdkEventType.TEXT_MESSAGE_CONTENT:
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            // Handle component data for SBOM table rendering
            if (event.components && Array.isArray(event.components)) {
              // If components exist, merge them (for incremental updates)
              if (lastMessage.components) {
                // Merge components, avoiding duplicates based on name+version+vendor
                const existingKeys = new Set(
                  lastMessage.components.map(c => `${c.name}-${c.version}-${c.vendor || ''}`)
                );
                const newComponents = event.components.filter(
                  c => !existingKeys.has(`${c.name}-${c.version}-${c.vendor || ''}`)
                );
                lastMessage.components = [...lastMessage.components, ...newComponents];
              } else {
                lastMessage.components = event.components;
              }
            }
            // Handle vulnerability data for vulnerabilities table rendering
            if (event.vulnerabilities && Array.isArray(event.vulnerabilities)) {
              // If vulnerabilities exist, merge them (for incremental updates)
              if (lastMessage.vulnerabilities) {
                // Merge vulnerabilities, avoiding duplicates based on Vulnerability Name
                const existingKeys = new Set(
                  lastMessage.vulnerabilities.map(v => v.vulnerability_name || v['Vulnerability Name'] || v.name || v.cve || '')
                );
                const newVulnerabilities = event.vulnerabilities.filter(
                  v => !existingKeys.has(v.vulnerability_name || v['Vulnerability Name'] || v.name || v.cve || '')
                );
                lastMessage.vulnerabilities = [...lastMessage.vulnerabilities, ...newVulnerabilities];
              } else {
                lastMessage.vulnerabilities = event.vulnerabilities;
              }
            }
            
            // Update text content - but clear it if we have components/vulnerabilities to avoid showing raw JSON
            if (event.components || event.vulnerabilities) {
              // Always clear content when we have structured data to prevent showing raw JSON
              lastMessage.content = '';
            } else if (lastMessage.components || lastMessage.vulnerabilities) {
              // If message already has components/vulnerabilities, don't add text content (might be raw JSON)
              // Keep existing content as is (should be empty)
              // Don't update content
            } else {
              // Only update content if we don't have structured data
              if (event.isFullContent) {
                lastMessage.content = event.content;
              } else {
                lastMessage.content += event.content;
              }
            }
          }
          return newMessages;
        });
        break;

      case AdkEventType.TEXT_MESSAGE_END:
        break;

      case AdkEventType.TOOL_CALL_START:
        setToolCalls(prev => ({
          ...prev,
          [event.toolCallId]: {
            name: event.toolCallName,
            args: typeof event.args === 'string' ? event.args : JSON.stringify(event.args || {}, null, 2),
            result: null,
            status: 'calling',
            parentMessageId: event.rawEvent?.parentMessageId
          }
        }));
        break;

      case AdkEventType.TOOL_CALL_RESULT:
        setToolCalls(prev => ({
          ...prev,
          [event.toolCallId]: {
            ...(prev[event.toolCallId] || { name: event.toolCallName, args: '', status: 'calling' }),
            name: event.toolCallName,
            result: typeof event.result === 'string' ? event.result : JSON.stringify(event.result || {}, null, 2),
            status: 'completed',
            messageId: event.rawEvent?.messageId
          }
        }));
        break;

      case AdkEventType.TOOL_CALL_END:
        setToolCalls(prev => {
          const existing = prev[event.toolCallId];
          if (existing) {
            return {
              ...prev,
              [event.toolCallId]: {
                ...existing,
                status: 'completed'
              }
            };
          }
          return prev;
        });
        break;

      case AdkEventType.ERROR:
        // Handle error events from the stream
        console.log('useChatStream: Received ERROR event:', event);
        const errorMessage = formatErrorMessage(event.error, event.errorCode, event.status);
        console.log('useChatStream: Formatted error message:', errorMessage);
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            // Update the last assistant message with error
            return [
              ...newMessages.slice(0, -1),
              {
                ...lastMessage,
                content: errorMessage,
                error: true,
                timestamp: new Date(event.timestamp)
              }
            ];
          } else {
            // Create new error message if no assistant message exists
            return [
              ...newMessages,
              {
                role: 'assistant',
                content: errorMessage,
                error: true,
                timestamp: new Date(event.timestamp)
              }
            ];
          }
        });
        break;

      case AdkEventType.RUN_FINISHED:
        break;

      case AdkEventType.UNKNOWN:
        if (process.env.NODE_ENV === 'development') {
          console.debug('Unknown ADK event format:', event.rawEvent);
        }
        break;
    }
  };

  const processSSEEvent = rawData => {
    // Track all events for debugging
    allEventsRef.current.push({
      timestamp: Date.now(),
      data: rawData,
      keys: Object.keys(rawData)
    });

    // Parse as ADK event
    const adkEvents = parseAdkEvent(rawData, processedChunkIds.current, {
      lastProcessedInvocationId
    });

    // Handle normalized ADK events
    for (const event of adkEvents) {
      handleAdkEvent(event);
    }
  };

  return { processSSEEvent };
}
